import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { getAuftragErsteller, type AuftragErstellerKontakt } from "@/lib/projectOverview";

export function CreatorContactCard({
  orderId,
  login,
  fallbackName,
  createdAt,
}: {
  orderId: number;
  login?: string | null;
  fallbackName?: string | null;
  createdAt?: string | null;
}) {
  const [contact, setContact] = useState<AuftragErstellerKontakt | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const requestId = useRef(0);

  const reload = useCallback(async () => {
    const token = ++requestId.current;
    const hasLogin = Boolean((login || "").trim());
    if (!hasLogin) {
      setContact(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const row = await getAuftragErsteller(orderId);
      if (token !== requestId.current) return;
      setContact(row);
      setError(null);
    } catch (err) {
      if (token !== requestId.current) return;
      setContact(null);
      setError(
        err instanceof ApiError ? err.message : "Kontaktdaten konnten nicht geladen werden.",
      );
    } finally {
      if (token === requestId.current) setLoading(false);
    }
  }, [login, orderId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    setContact(null);
    setError(null);
    setOpen(false);
  }, [orderId, login]);

  const name = displayName(contact, fallbackName, login);
  const createdLabel = createdAtLabel(contact?.created_at || createdAt);
  const phone = (contact?.mobil || contact?.telefon || "").trim();
  const mail = (contact?.email || "").trim();
  const reach = [phone, mail].filter(Boolean).join(" · ");
  const canOpen = Boolean((login || "").trim() || name !== "—");

  return (
    <div className="eh-po-contact-block">
      <div className="eh-po-contact-block-head">
        <p className="eh-po-info-panel-title">Erstellt von</p>
      </div>

      {canOpen ? (
        <button type="button" className="eh-po-contact" onClick={() => setOpen(true)} aria-haspopup="dialog">
          <span className="eh-po-contact-avatar" aria-hidden>
            {initials(name)}
          </span>
          <span className="eh-po-contact-copy">
            <span className="eh-po-contact-name">{name}</span>
            {createdLabel ? <span className="eh-po-contact-meta">{createdLabel}</span> : null}
            {reach ? <span className="eh-po-contact-meta">{reach}</span> : null}
          </span>
          <ChevronRightIcon className="eh-po-contact-chevron" aria-hidden />
        </button>
      ) : (
        <div className="eh-po-contact eh-po-contact--static">
          <span className="eh-po-contact-avatar" aria-hidden>
            ?
          </span>
          <span className="eh-po-contact-copy">
            <span className="eh-po-contact-name">—</span>
            {createdLabel ? <span className="eh-po-contact-meta">{createdLabel}</span> : null}
          </span>
        </div>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={name}
        description={createdLabel || undefined}
        size="md"
        footer={
          <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
            Schließen
          </Button>
        }
      >
        {loading && !contact ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : (
          <div className="space-y-4">
            {error ? (
              <Alert variant="error" title="Kontakt">
                {error}
              </Alert>
            ) : null}
            <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
              <DetailRow label="Name" value={name === "—" ? null : name} />
              <DetailRow label="Erstellt am" value={formatDateTime(contact?.created_at || createdAt)} />
              <DetailRow label="Telefon" value={contact?.telefon} hrefKind="tel" />
              <DetailRow label="Mobil" value={contact?.mobil} hrefKind="tel" />
              <DetailRow label="E-Mail" value={contact?.email} hrefKind="mail" wide />
            </dl>
          </div>
        )}
      </Dialog>
    </div>
  );
}

function DetailRow({
  label,
  value,
  hrefKind,
  wide,
}: {
  label: string;
  value?: string | null;
  hrefKind?: "tel" | "mail";
  wide?: boolean;
}) {
  const text = (value || "").trim();
  return (
    <div className={wide ? "sm:col-span-2" : undefined}>
      <dt className="text-label-tertiary">{label}</dt>
      <dd className="mt-0.5 text-label">
        {text && hrefKind === "tel" ? (
          <a href={`tel:${text}`} className="tabular-nums hover:text-accent hover:underline">
            {text}
          </a>
        ) : text && hrefKind === "mail" ? (
          <a href={`mailto:${text}`} className="hover:text-accent hover:underline">
            {text}
          </a>
        ) : (
          text || "—"
        )}
      </dd>
    </div>
  );
}

function displayName(
  contact: AuftragErstellerKontakt | null,
  fallbackName?: string | null,
  login?: string | null,
): string {
  const resolved = (contact?.display_name || "").trim();
  if (resolved) return resolved;
  const stored = (fallbackName || "").trim();
  if (stored) return stored;
  const raw = (login || "").trim();
  return raw || "—";
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function createdAtLabel(value: string | null | undefined): string {
  const formatted = formatDateTime(value);
  return formatted ? `Erstellt am ${formatted}` : "";
}

function initials(name: string): string {
  const parts = name
    .split(/\s+/)
    .map((part) => part.trim())
    .filter((part) => part && part !== "—");
  const letters = parts
    .slice(0, 2)
    .map((part) => part[0] ?? "")
    .join("")
    .toUpperCase();
  return letters || "?";
}
